import { supabase } from '../lib/supabase';

export interface GiftCardPromotion {
  id: string;
  business_id: string;
  name: string;
  description?: string;
  bonus_type: 'fixed' | 'percentage' | 'incremental';
  bonus_value: number;
  threshold_amount?: number; // For incremental bonus: the spending threshold
  start_date: string;
  end_date: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface PromotionBonus {
  promotion: GiftCardPromotion;
  bonusAmount: number;
  totalAmount: number;
}

export class GiftCardPromotionService {
  /**
   * Get all promotions for a business
   */
  static async getPromotions(businessId: string): Promise<GiftCardPromotion[]> {
    const { data, error } = await supabase
      .from('gift_card_promotions')
      .select('*')
      .eq('business_id', businessId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching promotions:', error);
      throw error;
    }

    return data || [];
  }

  /**
   * Get active promotion for current date
   */
  static async getActivePromotion(businessId: string): Promise<GiftCardPromotion | null> {
    const now = new Date().toISOString();
    
    const { data, error } = await supabase
      .from('gift_card_promotions')
      .select('*')
      .eq('business_id', businessId)
      .eq('is_active', true)
      .lte('start_date', now)
      .gte('end_date', now)
      .order('created_at', { ascending: false })
      .limit(1)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // No active promotion found
        return null;
      }
      console.error('Error fetching active promotion:', error);
      throw error;
    }

    return data;
  }

  /**
   * Calculate bonus amount based on promotion
   */
  static calculateBonus(amount: number, promotion: GiftCardPromotion): number {
    if (promotion.bonus_type === 'fixed') {
      // For fixed bonus: flat bonus amount regardless of purchase
      return promotion.bonus_value;
    } else if (promotion.bonus_type === 'percentage') {
      // For percentage bonus: amount * (percentage / 100)
      // E.g., spend $100, get 10% = $10 bonus
      return (amount * promotion.bonus_value) / 100;
    } else if (promotion.bonus_type === 'incremental') {
      // For incremental bonus: bonus_value for every threshold_amount spent
      // E.g., threshold=$50, bonus=$40
      // Spend $80 → 1 increment → $40 bonus
      // Spend $100 → 2 increments → $80 bonus
      // Spend $150 → 3 increments → $120 bonus
      if (!promotion.threshold_amount || promotion.threshold_amount <= 0) {
        return 0;
      }
      const increments = Math.floor(amount / promotion.threshold_amount);
      return increments * promotion.bonus_value;
    }
    return 0;
  }

  /**
   * Apply promotion to gift card purchase
   */
  static async applyPromotion(businessId: string, purchaseAmount: number): Promise<PromotionBonus | null> {
    try {
      const promotion = await this.getActivePromotion(businessId);
      
      if (!promotion) {
        return null;
      }

      const bonusAmount = this.calculateBonus(purchaseAmount, promotion);
      const totalAmount = purchaseAmount + bonusAmount;

      return {
        promotion,
        bonusAmount,
        totalAmount
      };
    } catch (error) {
      console.error('Error applying promotion:', error);
      return null;
    }
  }

  /**
   * Create a new promotion
   */
  static async createPromotion(data: Omit<GiftCardPromotion, 'id' | 'created_at' | 'updated_at'>): Promise<GiftCardPromotion> {
    const { data: promotion, error } = await supabase
      .from('gift_card_promotions')
      .insert(data)
      .select()
      .single();

    if (error) {
      console.error('Error creating promotion:', error);
      throw error;
    }

    return promotion;
  }

  /**
   * Update a promotion
   */
  static async updatePromotion(id: string, updates: Partial<Omit<GiftCardPromotion, 'id' | 'business_id' | 'created_at' | 'updated_at'>>): Promise<GiftCardPromotion> {
    const { data: promotion, error } = await supabase
      .from('gift_card_promotions')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating promotion:', error);
      throw error;
    }

    return promotion;
  }

  /**
   * Delete a promotion
   */
  static async deletePromotion(id: string): Promise<void> {
    const { error } = await supabase
      .from('gift_card_promotions')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting promotion:', error);
      throw error;
    }
  }

  /**
   * Toggle promotion active status
   */
  static async toggleActive(id: string, isActive: boolean): Promise<void> {
    const { error } = await supabase
      .from('gift_card_promotions')
      .update({ is_active: isActive })
      .eq('id', id);

    if (error) {
      console.error('Error toggling promotion:', error);
      throw error;
    }
  }
}

